/**
 * 녹음 바 관련 스타일 컴포넌트
 * 
 * 기능:
 * - 버튼 클릭 시 즉시 녹음 시작 후 바 표시
 * - 웨이브폼, 시간, 컨트롤 버튼
 * - 최대 3시간 녹음 지원
 */
import styled, { keyframes } from 'styled-components';

const waveAnimation = keyframes`
  0%, 100% {
    height: 4px;
  }
  50% {
    height: 24px;
  }
`;

// 오버레이 (전체 화면 - 외부 클릭 감지용)
export const RecordingModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10003;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 80px;
`;

// 녹음 바 컨테이너
export const RecordingModal = styled.div`
  background-color: ${props => props.theme.colors.white};
  border: 1px solid ${props => props.theme.colors.border.ce};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.sm};
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.10);
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
`;

// 웨이브폼 컨테이너
export const WaveformContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  height: 40px;
  padding: 0 4px;
`;

// 웨이브폼 바
export const WaveformBar = styled.div<{ $isActive: boolean; $delay: number }>`
  width: 2px;
  height: 4px;
  background: linear-gradient(91deg, ${props => props.$isActive ? '#00A83E' : '#CBCBCB'} -0.96%, ${props => props.$isActive ? '#00A83E' : '#323030'} 101.51%);
  border-radius: 2px;
  animation: ${props => props.$isActive ? waveAnimation : 'none'} 0.5s ease-in-out infinite;
  animation-delay: ${props => props.$delay}ms;
`;

// 시간 표시
export const RecordingTimer = styled.div<{ $isRecording?: boolean; $isPaused?: boolean }>`
  font-size: ${props => props.theme.fontSize.xs};
  font-weight: ${props => props.theme.fontWeight.normal};
  color: ${props => props.theme.colors.book.color6b};
  text-align: center;
`;

// 컨트롤 버튼 그룹
export const RecordingButtonGroup = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;

// 아이콘 버튼 (재생, 녹음, 정지)
export const IconButton = styled.button<{ $variant?: 'play' | 'record' | 'stop' | 'pause' }>`
  width: 30px;
  height: 30px;
  border: none;
  border-radius: ${props => props.theme.borderRadius.lg};
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  background-color: ${props => {
    switch (props.$variant) {
      case 'record': return '#A41313';
      case 'stop': return '#F4F4F4';
      case 'pause': return '#F4F4F4';
      default: return '#F4F4F4';
    }
  }};
  
  &:hover:not(:disabled) {
    background-color: ${props => {
      switch (props.$variant) {
        case 'record': return '#660404';
        case 'stop': return '#dfdfdf';
        case 'pause': return '#dfdfdf';
        default: return '#dfdfdf';
      }
    }};
  }

  & svg {
    width: 14px;
    height: 14px;
  }
  
  
  &:disabled {
    opacity: 0.8;
    cursor: not-allowed;
    transform: none;
  }
  
`;

// 닫기 버튼
export const CloseButton = styled.button`
  width: 30px;
  height: 30px;
  border: none;
  border-radius: ${props => props.theme.borderRadius.lg};
  background-color: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f4f4f4;

  outline: none;
  
  &:hover {
    background-color: #dfdfdf;
  }
  
  svg {
    width: 16px;
    height: 16px;
    color: #6b6b6b;
  }
`;

// 업로드 버튼
export const UploadButton = styled.button`
  padding: 8px 14px;
  border: none;
  border-radius: 8px;
  background-color: #3b82f6;
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    background-color: #2563eb;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// 녹음 완료 후 하단 영역
export const RecordingFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  padding-top: 12px;
  border-top: 1px solid #333;
  margin-top: 8px;
`;

// 파일명 입력 필드
export const FileNameInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: none;
  border-radius: 8px;
  background-color: #2a2a2a;
  color: #ffffff;
  font-size: 13px;
  outline: none;
  
  &::placeholder {
    color: #6b7280;
  }
  
  &:focus {
    background-color: #333;
  }
`;

// 하단 버튼 그룹
export const FooterButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

// 삭제 버튼
export const DeleteButton = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  background-color: transparent;
  color: #9ca3af;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    color: #ef4444;
    background-color: rgba(239, 68, 68, 0.1);
  }
`;

// 저장 버튼
export const SaveButton = styled.button`
  padding: 8px 20px;
  border: none;
  border-radius: 8px;
  background-color: transparent;
  color: #9ca3af;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    color: #3b82f6;
    background-color: rgba(59, 130, 246, 0.1);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// 기존 스타일 유지 (호환성 - 사용하지 않음)
export const RecordingTitle = styled.h3`
  display: none;
`;

export const RecordingIndicator = styled.div<{ $isRecording?: boolean; $isPaused?: boolean }>`
  display: none;
`;

export const RecordingDot = styled.div<{ $isRecording?: boolean; $isPaused?: boolean }>`
  display: none;
`;

export const RecordingProgress = styled.div`
  display: none;
`;

export const RecordingProgressBar = styled.div<{ $progress?: number }>`
  display: none;
`;

export const RecordingButton = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' | 'warning' | 'success' }>`
  display: none;
`;

export const RecordingMessage = styled.p`
  display: none;
`;
